import { User, PlanTier, PlanFeature, PlanConfig, AuthProviderConfig, SupportTicket, SystemLog, PaymentConfig, Coupon, Invoice } from '../types';

// Default Mock User
let mockUser: User = {
  id: "user_123",
  name: "Demo User",
  email: "demo@example.com",
  role: "user",
  plan: PlanTier.KANBAN,
  subscriptionInterval: null,
  nextBillingDate: null,
  lastInvoicedDate: null,
  currentCouponCode: null,
  currentDiscountPercent: 0,
  avatarUrl: "https://ui-avatars.com/api/?name=Demo+User&background=random",
  phoneNumber: "+1 555 000 0000",
  integrations: [],
  isMfaEnabled: false,
  isActive: true
};

// Default Users List
const defaultUsersList: User[] = [
  mockUser,
  {
    id: "user_456",
    name: "John Doe",
    email: "john@example.com",
    role: "user",
    plan: PlanTier.PRO,
    avatarUrl: "https://ui-avatars.com/api/?name=John+Doe&background=random",
    phoneNumber: "+1 555 123 4567",
    isMfaEnabled: false,
    isActive: true
  },
  {
    id: "user_789",
    name: "Bad Actor",
    email: "blocked@example.com",
    role: "user",
    plan: PlanTier.KANBAN,
    avatarUrl: "https://ui-avatars.com/api/?name=Bad+Actor&background=random",
    phoneNumber: "+1 555 999 9999",
    isMfaEnabled: false,
    isActive: false
  }
];

let mockUsersList: User[] = [];
let mockLogs: SystemLog[] = [];
let mockCoupons: Coupon[] = [];
let mockInvoices: Invoice[] = [];
let mockPendingInvites: any[] = [];

// Helper to log activity
const logActivity = (
  level: 'info' | 'warning' | 'error' | 'critical', 
  source: string, 
  message: string, 
  userId?: string, 
  userName?: string,
  details?: string
) => {
  const newLog: SystemLog = {
    id: "LOG-" + Date.now() + Math.random().toString(36).substr(2, 5),
    level,
    source,
    message,
    details,
    userId,
    userName,
    timestamp: new Date().toISOString()
  };
  mockLogs = [newLog, ...mockLogs];
  localStorage.setItem('pf_logs', JSON.stringify(mockLogs));
};

// 90-Day Retention Cleanup
const cleanupLogs = () => {
  const ninetyDaysAgo = new Date();
  ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
  
  const originalCount = mockLogs.length;
  mockLogs = mockLogs.filter(log => new Date(log.timestamp) > ninetyDaysAgo);
  
  if (mockLogs.length !== originalCount) {
    localStorage.setItem('pf_logs', JSON.stringify(mockLogs));
    console.log(`[System] Cleaned up ${originalCount - mockLogs.length} old logs.`);
  }
};

const loadData = () => {
  // Users
  const storedUsers = localStorage.getItem('pf_users_list');
  if (storedUsers) {
    mockUsersList = JSON.parse(storedUsers);
  } else {
    mockUsersList = [...defaultUsersList];
    localStorage.setItem('pf_users_list', JSON.stringify(mockUsersList));
  }

  // Logs
  const storedLogs = localStorage.getItem('pf_logs');
  if (storedLogs) {
    mockLogs = JSON.parse(storedLogs);
  } else {
    mockLogs = [
      { id: '1', level: 'info', source: 'system', message: 'System initialized', timestamp: new Date().toISOString() },
    ];
    localStorage.setItem('pf_logs', JSON.stringify(mockLogs));
  }
  
  // Coupons
  const storedCoupons = localStorage.getItem('pf_coupons');
  if (storedCoupons) {
    mockCoupons = JSON.parse(storedCoupons);
  } else {
    mockCoupons = [
      { id: 'c_1', code: 'WELCOME20', discountPercent: 20, planTier: 'All', expiryDate: '2025-12-31', isActive: true },
      { id: 'c_2', code: 'PRO50', discountPercent: 50, planTier: PlanTier.PRO, expiryDate: '2025-12-31', isActive: true },
      { id: 'c_3', code: 'FREE100', discountPercent: 100, planTier: 'All', expiryDate: '2025-12-31', isActive: true }
    ];
    localStorage.setItem('pf_coupons', JSON.stringify(mockCoupons));
  }

  // Invoices
  const storedInvoices = localStorage.getItem('pf_invoices');
  if (storedInvoices) {
      mockInvoices = JSON.parse(storedInvoices);
  } else {
      mockInvoices = [];
      localStorage.setItem('pf_invoices', JSON.stringify(mockInvoices));
  }

  // Pending Invites
  const storedInvites = localStorage.getItem('pf_pending_invites');
  if (storedInvites) {
    mockPendingInvites = JSON.parse(storedInvites);
  } else {
    mockPendingInvites = [
      {
        id: 1,
        invited_email: 'alice@example.com',
        invited_by_user_id: 'user_123',
        project_id: 'proj_1',
        task_id: null,
        coupon_code: 'WELCOME20',
        task_title: null,
        project_name: 'Marketing Campaign',
        inviter_name: 'Demo User',
        inviter_email: 'demo@example.com',
        invite_token: 'token_abc123',
        status: 'pending',
        expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        accepted_at: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      {
        id: 2,
        invited_email: 'bob@example.com',
        invited_by_user_id: 'user_123',
        project_id: 'proj_2',
        task_id: null,
        coupon_code: null,
        task_title: null,
        project_name: 'Product Development',
        inviter_name: 'Demo User',
        inviter_email: 'demo@example.com',
        invite_token: 'token_def456',
        status: 'accepted',
        expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        accepted_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        created_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
        updated_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString()
      }
    ];
    localStorage.setItem('pf_pending_invites', JSON.stringify(mockPendingInvites));
  }
  
  cleanupLogs();
};

loadData();

const saveMockUsersList = () => {
  localStorage.setItem('pf_users_list', JSON.stringify(mockUsersList));
};

const BLOCKED_MSG = "Contact the administrator at info@kanbanboard.org , you have been blocked due to violation";

const defaultAuthProviders: AuthProviderConfig[] = [
  { id: 'google', name: 'Google Workspace', isEnabled: true, clientId: '', clientSecret: '' },
  { id: 'microsoft', name: 'Microsoft Azure AD', isEnabled: true, clientId: '', clientSecret: '' },
  { id: 'linkedin', name: 'LinkedIn', isEnabled: false, clientId: '', clientSecret: '' },
  { id: 'github', name: 'GitHub', isEnabled: false, clientId: '', clientSecret: '' }
];

const defaultPaymentConfig: PaymentConfig = {
  stripePublicKey: '',
  stripeSecretKey: '',
  paypalClientId: '',
  paypalSecret: '',
  paypalDonationLink: '',
  upiDonationId: ''
};

export const mockDB = {
  getUser: async (): Promise<User> => {
    const stored = localStorage.getItem('pf_user');
    if (stored) {
      const u = JSON.parse(stored);
      if (typeof u.isActive === 'undefined') { u.isActive = true; }
      return u;
    }
    return mockUser;
  },
  
  saveUser: async (u: User) => {
    localStorage.setItem('pf_user', JSON.stringify(u));
    const idx = mockUsersList.findIndex(mu => mu.id === u.id);
    if (idx > -1) { 
      mockUsersList[idx] = u; 
    } else { 
      mockUsersList.push(u); 
    }
    saveMockUsersList();
    logActivity('info', 'user_update', `User profile updated`, u.id, u.name);
    return u;
  },

  getAllUsers: async () => {
    const current = await mockDB.getUser();
    if (!mockUsersList.find(u => u.id === current.id)) { 
      mockUsersList.push(current); 
      saveMockUsersList();
    }
    return [...mockUsersList];
  },

  updateUserStatus: async (userId: string, isActive: boolean) => {
    const userIndex = mockUsersList.findIndex(u => u.id === userId);
    if (userIndex > -1) {
      const user = mockUsersList[userIndex];
      const updatedUser = { ...user, isActive };
      mockUsersList[userIndex] = updatedUser;
      saveMockUsersList();
      const current = await mockDB.getUser();
      if (current.id === userId) { 
        localStorage.setItem('pf_user', JSON.stringify(updatedUser));
      }
      logActivity('warning', 'admin_action', `User ${user.name} was ${isActive ? 'activated' : 'deactivated'}`, userId, user.name);
      return updatedUser;
    }
    throw new Error("User not found");
  },

  updateUserRoleAndPlan: async (userId: string, role: string, plan: PlanTier) => {
    const userIndex = mockUsersList.findIndex(u => u.id === userId);
    if (userIndex > -1) {
      const user = mockUsersList[userIndex];
      const updatedUser: User = { ...user, role: role as 'user' | 'admin', plan };
      mockUsersList[userIndex] = updatedUser;
      saveMockUsersList();

      // Update current session user if ID matches
      const currentUser = await mockDB.getUser();
      if (currentUser.id === userId) {
        localStorage.setItem('pf_user', JSON.stringify(updatedUser));
      }

      logActivity('info', 'admin_action', `User ${user.name} updated: Role=${role}, Plan=${plan}`, userId, user.name);
      return "User updated successfully";
    }
    return "User not found";
  },

  socialLogin: async (provider: string, email: string, name: string, avatar: string) => {
    const existingUser = mockUsersList.find(u => u.email === email);
    if (existingUser && existingUser.isActive === false) { 
      logActivity('warning', 'auth', `Blocked user attempted login: ${email}`, existingUser.id, existingUser.name);
      throw new Error(BLOCKED_MSG); 
    }
    let user = existingUser || { 
      id: "user_" + Date.now(), 
      name, 
      email, 
      role: 'user', 
      plan: PlanTier.KANBAN, 
      avatarUrl: avatar, 
      phoneNumber: '', 
      isMfaEnabled: false, 
      isActive: true 
    };
    if (user.isMfaEnabled) { 
      return { mfaRequired: true, userId: user.id, message: "Enter code sent to " + user.phoneNumber }; 
    }
    await mockDB.saveUser(user);
    logActivity('info', 'auth', `User logged in via ${provider}`, user.id, user.name);
    return { user, token: 'mock_token_' + Date.now() };
  },

  verifyMfa: async (userId: string, code: string) => {
    if (code === '123456') { 
      const u = await mockDB.getUser(); 
      logActivity('info', 'auth', `MFA Verified`, u.id, u.name);
      return { user: u, token: 'mock_mfa_token_' + Date.now() }; 
    }
    throw new Error('Invalid code');
  },
  
  // --- LOGS EXTENSION ---
  getSystemLogs: async (userId?: string): Promise<SystemLog[]> => {
    cleanupLogs();
    const logs = [...mockLogs];
    if (userId) return logs.filter(l => l.userId === userId);
    return logs;
  },

  deleteUserLogs: async (userId: string) => {
    mockLogs = mockLogs.filter(l => l.userId !== userId);
    localStorage.setItem('pf_logs', JSON.stringify(mockLogs));
    logActivity('info', 'admin_action', `Logs cleared for user ID: ${userId}`);
    return true;
  },
  
  // Plans & Features
  getPlans: async (): Promise<PlanConfig[]> => {
      const stored = localStorage.getItem('pf_plans');
      const defaults = [
          { tier: PlanTier.KANBAN, price: 0, currency: 'USD', isActive: true, description: 'Essential features for individuals' },
          { tier: PlanTier.PRO, price: 29, currency: 'USD', isActive: true, description: 'Advanced AI and integrations for power users' },
          { tier: PlanTier.ENTERPRISE, price: 99, currency: 'USD', isActive: true, description: 'Custom solutions for large teams' }
      ];

      if (stored) {
        let plans: PlanConfig[] = JSON.parse(stored);
        let changed = false;

        // Migration: Rename 'Free' to 'Kanban' if exists
        const freePlanIndex = plans.findIndex(p => p.tier === 'Free' as any);
        if (freePlanIndex > -1) {
            plans[freePlanIndex].tier = PlanTier.KANBAN;
            plans[freePlanIndex].description = defaults[0].description;
            changed = true;
        }

        // Migration: Add Kanban if missing (e.g. from old data)
        if (!plans.find(p => p.tier === PlanTier.KANBAN)) {
            plans.unshift(defaults[0]);
            changed = true;
        }

        if (changed) {
            localStorage.setItem('pf_plans', JSON.stringify(plans));
        }
        return plans;
      }

      localStorage.setItem('pf_plans', JSON.stringify(defaults));
      return defaults;
  },
  updatePlan: async (plan: PlanConfig) => {
      const plans = await mockDB.getPlans();
      const updated = plans.map(p => p.tier === plan.tier ? plan : p);
      localStorage.setItem('pf_plans', JSON.stringify(updated));
      return plan;
  },
  getPlanFeatures: async (): Promise<PlanFeature[]> => {
      const stored = localStorage.getItem('pf_features');
      if (stored) {
          let features: PlanFeature[] = JSON.parse(stored);
          let changed = false;
          // Migration: Update features tied to 'Free' plan to 'Kanban'
          features = features.map(f => {
              if (f.plan_tier === 'Free' as any) {
                  changed = true;
                  return { ...f, plan_tier: PlanTier.KANBAN };
              }
              return f;
          });
          if (changed) {
              localStorage.setItem('pf_features', JSON.stringify(features));
          }
          return features;
      }
      return [];
  },
  updatePlanFeature: async (feature: PlanFeature) => {
      const features = await mockDB.getPlanFeatures();
      const updated = features.map(f => f.id === feature.id ? feature : f);
      localStorage.setItem('pf_features', JSON.stringify(updated));
      return feature;
  },

  // Coupons
  getCoupons: async (): Promise<Coupon[]> => {
    const stored = localStorage.getItem('pf_coupons');
    if (stored) {
        let coupons: Coupon[] = JSON.parse(stored);
        let changed = false;
        // Migration: Update coupons tied to 'Free' plan
        coupons = coupons.map(c => {
            if (c.planTier === 'Free' as any) {
                changed = true;
                return { ...c, planTier: PlanTier.KANBAN };
            }
            return c;
        });
        if (changed) {
            localStorage.setItem('pf_coupons', JSON.stringify(coupons));
        }
        return coupons;
    }
    return mockCoupons;
  },

  createCoupon: async (coupon: Coupon) => {
    const coupons = await mockDB.getCoupons();
    if (coupons.find(c => c.code === coupon.code)) throw new Error("Coupon code already exists");
    
    const newCoupon = { ...coupon, id: 'c_' + Date.now() };
    const updated = [...coupons, newCoupon];
    localStorage.setItem('pf_coupons', JSON.stringify(updated));
    logActivity('info', 'admin_action', `Coupon created: ${coupon.code}`);
    return newCoupon;
  },

  deleteCoupon: async (id: string) => {
    const coupons = await mockDB.getCoupons();
    const updated = coupons.filter(c => c.id !== id);
    localStorage.setItem('pf_coupons', JSON.stringify(updated));
    logActivity('info', 'admin_action', `Coupon deleted: ${id}`);
    return true;
  },

  validateCoupon: async (code: string, planTier: PlanTier) => {
    const coupons = await mockDB.getCoupons();
    const coupon = coupons.find(c => c.code === code);

    if (!coupon) throw new Error("Invalid coupon code");
    if (!coupon.isActive) throw new Error("Coupon is inactive");
    
    const now = new Date();
    const expiry = new Date(coupon.expiryDate);
    if (now > expiry) throw new Error("Coupon has expired");

    if (coupon.planTier !== 'All' && coupon.planTier !== planTier) {
      throw new Error(`Coupon is valid only for ${coupon.planTier} plan`);
    }

    return coupon;
  },
  
  // Auth Providers
  getAuthProviders: async (): Promise<AuthProviderConfig[]> => {
      const stored = localStorage.getItem('pf_auth_providers');
      if (stored) return JSON.parse(stored);
      localStorage.setItem('pf_auth_providers', JSON.stringify(defaultAuthProviders));
      return defaultAuthProviders;
  },
  updateAuthProvider: async (id: string, data: Partial<AuthProviderConfig>) => {
      const providers = await mockDB.getAuthProviders();
      const updated = providers.map(p => p.id === id ? { ...p, ...data } : p);
      localStorage.setItem('pf_auth_providers', JSON.stringify(updated));
      return updated.find(p => p.id === id);
  },
  createAuthProvider: async (provider: AuthProviderConfig) => {
      const providers = await mockDB.getAuthProviders();
      if (providers.find(p => p.id === provider.id)) throw new Error("Provider ID already exists");
      const updated = [...providers, provider];
      localStorage.setItem('pf_auth_providers', JSON.stringify(updated));
      return provider;
  },

  // Payment Config
  getPaymentConfig: async (): Promise<PaymentConfig> => {
    const stored = localStorage.getItem('pf_payment_config');
    if (stored) return JSON.parse(stored);
    localStorage.setItem('pf_payment_config', JSON.stringify(defaultPaymentConfig));
    return defaultPaymentConfig;
  },
  updatePaymentConfig: async (config: PaymentConfig): Promise<PaymentConfig> => {
    localStorage.setItem('pf_payment_config', JSON.stringify(config));
    logActivity('info', 'admin_action', 'Payment configuration updated');
    return config;
  },
  
  // Support Tickets
  getSupportTickets: async (): Promise<SupportTicket[]> => {
    const stored = localStorage.getItem('pf_tickets');
    return stored ? JSON.parse(stored) : [];
  },
  createSupportTicket: async (data: any) => {
    const tickets = await mockDB.getSupportTickets();
    const newTicket = { ...data, id: "TKT-" + Date.now(), messages: [{ sender: 'user', text: data.message, timestamp: new Date().toISOString() }], created_at: new Date().toISOString() };
    localStorage.setItem('pf_tickets', JSON.stringify([newTicket, ...tickets]));
    logActivity('info', 'support', `New ticket created: ${newTicket.id}`, data.userId);
    return newTicket;
  },
  
  requestEnterpriseUpgrade: async (user: User) => {
    const ticketId = "ENT-UPG-" + Date.now();
    logActivity('info', 'billing', `Enterprise Upgrade Request: ${ticketId}`, user.id, user.name);
    return { ticketId, message: "Request created successfully" };
  },

  deleteUser: async (userId: string) => {
    const u = mockUsersList.find(u => u.id === userId);
    mockUsersList = mockUsersList.filter(u => u.id !== userId);
    saveMockUsersList();
    logActivity('critical', 'admin_action', `User deleted: ${u?.email || userId}`);
  },

  // Invoices & Checkout
  getInvoices: async (): Promise<Invoice[]> => {
    const stored = localStorage.getItem('pf_invoices');
    return stored ? JSON.parse(stored) : mockInvoices;
  },

    createCheckoutSession: async (plan: PlanTier, interval: 'month' | 'year', couponCode?: string) => {
      const user = await mockDB.getUser();
      const plans = await mockDB.getPlans();
      const selectedPlan = plans.find(p => p.tier === plan);
      const basePrice = selectedPlan ? selectedPlan.price : 0;
      const intervalPrice = interval === 'year' ? basePrice * 12 : basePrice;

      let discount = 0;
      let validCoupon: Coupon | undefined;

      if (couponCode) {
        try {
          validCoupon = await mockDB.validateCoupon(couponCode, plan);
          discount = validCoupon.discountPercent;
        } catch (e) {
          console.warn("Invalid coupon in checkout", e);
        }
      }

      const discountedAmount = Math.max(0, intervalPrice * (1 - discount / 100));

        // Persist subscription metadata for mock recurring billing
        const nextDate = new Date();
        nextDate.setMonth(nextDate.getMonth() + (interval === 'year' ? 12 : 1));
        const updatedUser: User = {
        ...user,
        plan,
        subscriptionInterval: interval,
        lastInvoicedDate: new Date().toISOString(),
        nextBillingDate: nextDate.toISOString(),
        currentCouponCode: couponCode || null,
        currentDiscountPercent: discount
        };
        await mockDB.saveUser(updatedUser);

        if (discount === 100) {
          // 100% Discount Logic - Bypass Payment Gateway
          await mockDB.updateUserRoleAndPlan(user.id, user.role, plan);
          
          const newInvoice: Invoice = {
            id: 'inv_' + Date.now(),
            user_id: user.id,
            amount: 0,
            status: 'paid',
            issued_date: new Date().toISOString(),
            description: `${plan} Plan Subscription (${interval === 'year' ? 'Yearly' : 'Monthly'}) with 100% Discount${couponCode ? ` - ${couponCode}` : ''}`,
            pdf_url: '#',
            interval,
            couponCode,
            discountPercent: discount
          };

          const invoices = await mockDB.getInvoices();
          invoices.unshift(newInvoice);
          localStorage.setItem('pf_invoices', JSON.stringify(invoices));
          
          logActivity('info', 'billing', `User ${user.name} subscribed to ${plan} with 100% off coupon ${couponCode}`, user.id, user.name);
          
          // Return valid URL for redirection (back to app)
          return { url: window.location.href.split('#')[0] }; 
        }

        // Generate a pending invoice for visibility in Payments tab (mock gateway flow)
        const pendingInvoice: Invoice = {
          id: 'inv_' + Date.now(),
          user_id: user.id,
          amount: parseFloat(discountedAmount.toFixed(2)),
          status: 'open',
          issued_date: new Date().toISOString(),
          description: `${plan} Plan Subscription (${interval === 'year' ? 'Yearly' : 'Monthly'})`,
          pdf_url: '#',
          interval,
          couponCode,
          discountPercent: discount
        };
        const invoices = await mockDB.getInvoices();
        invoices.unshift(pendingInvoice);
        localStorage.setItem('pf_invoices', JSON.stringify(invoices));

        // Standard Mock Payment Gateway
        return { url: 'https://checkout.stripe.com/test-checkout' };
    },
  
  // Projects - using localStorage for persistence
  getProjects: async (userId: string) => {
    try {
      const stored = localStorage.getItem('pf_projects');
      if (stored) {
        const allProjects = JSON.parse(stored);
        return allProjects.filter((p: any) => p.userId === userId);
      }
    } catch (e) {
      console.warn('Failed to load projects from localStorage', e);
    }
    return [];
  },
  createProject: async (p: any) => {
    try {
      const stored = localStorage.getItem('pf_projects');
      const allProjects = stored ? JSON.parse(stored) : [];
      allProjects.push(p);
      localStorage.setItem('pf_projects', JSON.stringify(allProjects));
    } catch (e) {
      console.warn('Failed to save project to localStorage', e);
    }
    return p;
  },
  deleteProject: async (id: string) => {
    try {
      const stored = localStorage.getItem('pf_projects');
      if (stored) {
        const allProjects = JSON.parse(stored);
        const filtered = allProjects.filter((p: any) => p.id !== id);
        localStorage.setItem('pf_projects', JSON.stringify(filtered));
      }
    } catch (e) {
      console.warn('Failed to delete project from localStorage', e);
    }
  },
  updateProjectTasks: async (pid: string, tasks: any[]) => {
    try {
      const stored = localStorage.getItem('pf_projects');
      if (stored) {
        const allProjects = JSON.parse(stored);
        const projectIndex = allProjects.findIndex((p: any) => p.id === pid);
        if (projectIndex >= 0) {
          allProjects[projectIndex].tasks = tasks;
          localStorage.setItem('pf_projects', JSON.stringify(allProjects));
        }
      }
    } catch (e) {
      console.warn('Failed to update project tasks in localStorage', e);
    }
  },
  replyToSupportTicket: async (id: string, msg: string) => {},
  updateTicketStatus: async (id: string, status: any) => {},
  getCRMLeads: async () => [],
  getCRMLeadDetails: async (id: number) => null,
  createCRMLead: async (lead: any) => {},
  addCRMComment: async (id: number, content: string) => {},
  linkReminderToLead: async (leadId: number, title: string, date: string) => {},
  getReminders: async () => [],
  createReminder: async (r: any) => {},
  updateReminder: async (id: number, r: any) => {},
  getFAQs: async () => [],
  createFAQ: async (f: any) => {},
  updateFAQ: async (id: number, f: any) => {},
  deleteFAQ: async (id: number) => {},
  getCMSPage: async (slug: string) => null,
  updateCMSPage: async (slug: string, content: string) => {},
  verifyPendingChange: async (token: string) => null,
  completePlanChange: async (token: string, success: boolean) => {},
  
  // Pending Invites methods
  pendingInvites: mockPendingInvites,
  getSentInvites: async (status?: string) => {
    const current = await mockDB.getUser();
    let filtered = mockPendingInvites.filter(i => i.invited_by_user_id === current.id);
    if (status && status !== 'all') {
      filtered = filtered.filter(i => i.status === status);
    }
    return filtered;
  },
  resendInvite: async (inviteId: number, couponCode?: string) => {
    const invite = mockPendingInvites.find(i => i.id === inviteId);
    if (invite) {
      invite.status = 'pending';
      invite.coupon_code = couponCode;
      invite.expires_at = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
      invite.updated_at = new Date().toISOString();
      localStorage.setItem('pf_pending_invites', JSON.stringify(mockPendingInvites));
    }
    return { success: true, message: 'Invite resent successfully' };
  },
  getInviteStats: async () => {
    const current = await mockDB.getUser();
    const userInvites = mockPendingInvites.filter(i => i.invited_by_user_id === current.id);
    return {
      total: userInvites.length,
      pending: userInvites.filter(i => i.status === 'pending').length,
      accepted: userInvites.filter(i => i.status === 'accepted').length,
      rejected: userInvites.filter(i => i.status === 'rejected').length,
      expired: userInvites.filter(i => i.status === 'expired').length
    };
  }
};